import React, { useContext, useCallback } from 'react';
import moment from 'moment-timezone';
import { Menu } from 'antd';
import { useSelector } from 'react-redux';
import { ManualAlert, ManualAlertEvent } from '@conviva/portal-graphql-schema';
import { isAdminRoleSelector } from '@store/common/selectors';
import { getMetricInfo } from '@src/pages/Alert/manualAlert/constants';
import { FormActionContext } from '@src/pages/Alert/manualAlert/form/context';
import { convertValue } from '@src/pages/Alert/manualAlert/shared';
import usePageTitle from '@src/hooks/usePageTitle';
import HelpIco from '@icons/ovb/help.svg';
import { EVENT_DT_FORMAT } from '@utils/constants';
import useSetManualAlertExport from './useSetManualAlertExport';
import {
  Title,
  SubTitle,
  Wrapper,
  Text,
  Info,
  AlertName,
  StyledDropDown,
  AlertText,
  StyledToolTip,
} from './styled';

export const Priorities = {
  0: 'P1',
  1: 'P2',
  2: 'P3',
  3: 'Not Set',
};

export const convertCooldown = (cooldown: number) => {
  const switchCondition = cooldown > 0 && cooldown < 60 ? 59 : cooldown; // 59 is an arbitrary value;
  switch (switchCondition) {
    case 0:
      return '0';
    case 59:
      return `${cooldown} minutes`;
    case 60:
      return '1 hour';
    default:
      return `${cooldown / 60} hours`;
  }
};

export const getSampleCompare = (eventSamples, samples) => {
  if (eventSamples > samples) {
    return '>';
  }
  if (eventSamples < samples) {
    return '<';
  }
  return '=';
};

interface IProps {
  event: ManualAlertEvent;
  alert: ManualAlert;
}

const EventDetail: React.FC<IProps> = ({ event, alert }) => {
  const { time: eventTime, samples: eventSamples, value: eventValue, window: eventWindow } = event;
  const { name, metric, threshold, direction, priority, cooldown, samples, changedAt } = alert;

  const isAdmin = useSelector(isAdminRoleSelector);

  const {
    actions: { onEdit },
  } = useContext(FormActionContext);

  // CE-7390 : Show only event info if alert was edited after the event happened
  const hideAlertInfo = moment(changedAt).isAfter(eventTime);

  const updateAlertMessage = `The Alert definition was last updated on ${moment(changedAt).format('MMM DD YYYY HH:mm')}`;

  usePageTitle({ text: `Alert - ${name}` });
  const clickMenu = useCallback(() => {
    onEdit(alert);
  }, [alert, onEdit]);
  const metricsInfo = getMetricInfo();

  const metricInfo = metricsInfo[metric];
  const menu = (
    <Menu onClick={clickMenu}>
      <Menu.Item key="edit">Edit</Menu.Item>
    </Menu>
  );

  const summary: any = {
    alertName: name,
    metricName: metricsInfo[metric].name,
    timeRange: `${moment(eventTime).subtract(eventWindow, 'minutes').format('HH:mm')} - ${moment(eventTime).format(EVENT_DT_FORMAT)}`,
    cooldown: convertCooldown(cooldown),
    priority: Priorities[priority],
  };

  if (hideAlertInfo) {
    summary.rule = `${convertValue(eventValue, metricInfo.units)} ${metricInfo.units} `;
    summary.threshold = `${eventSamples} ${metricInfo.cm_label}`;
  } else {
    summary.rule = `${convertValue(eventValue, metricInfo.units)} ${direction} ${convertValue(threshold, metricInfo.units)} ${metricInfo.units}`;
    summary.threshold = `${eventSamples} ${getSampleCompare(eventSamples, samples)} ${samples} ${metricInfo.cm_label}`;
  }

  useSetManualAlertExport(summary);

  return (
    <Wrapper>
      <Title title={name}>
        <AlertName>{name}</AlertName>
        {isAdmin && <StyledDropDown isChildrenMenu>{menu}</StyledDropDown>}
      </Title>

      <Info>
        <SubTitle>{summary.metricName}</SubTitle>
        {hideAlertInfo ? (
          <>
            <AlertText>
              {`${convertValue(eventValue, metricInfo.units)} ${metricInfo.units} `}
              <StyledToolTip title={updateAlertMessage}>
                <HelpIco />
              </StyledToolTip>
            </AlertText>
          </>
        ) : (
          <>
            <Text>{`${convertValue(eventValue, metricInfo.units)} ${direction} ${convertValue(threshold, metricInfo.units)} ${metricInfo.units}`}</Text>
          </>
        )}

        <SubTitle>Audience Threshold</SubTitle>
        <Text>{summary.threshold}</Text>

        <SubTitle>Start Time - End Time</SubTitle>
        <Text>{summary.timeRange}</Text>

        <SubTitle>Cool Down Period</SubTitle>
        <Text>{summary.cooldown}</Text>

        <SubTitle>Priority</SubTitle>
        <Text>{summary.priority}</Text>
      </Info>
    </Wrapper>
  );
};

export default EventDetail;
